export interface ModerationResult {
  isSafe: boolean;
  flaggedCategories: string[];
  confidence: number;
}

/**
 * Check if content is safe using OpenAI Moderation API or fallback regex
 */
export async function safeRenderAI(text: string): Promise<ModerationResult> {
  try {
    // Use backend API for content moderation
    try {
      return await moderateWithBackend(text);
    } catch (error) {
      console.warn('Backend moderation failed, falling back to regex:', error);
      // Fallback to regex/heuristic filter
      return moderateWithRegex(text);
    }
  } catch (error) {
    console.error('Error in content moderation:', error);
    // If moderation fails, err on the side of caution
    return {
      isSafe: false,
      flaggedCategories: ['error'],
      confidence: 0.5
    };
  }
}

/**
 * Moderate content using backend API
 */
async function moderateWithBackend(text: string): Promise<ModerationResult> {
  try {
    const response = await fetch('/api/moderate-content', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ text }),
    });

    if (!response.ok) {
      throw new Error('Backend moderation request failed');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Backend moderation error:', error);
    throw error;
  }
}

/**
 * Legacy function - now handled by backend
 * @deprecated Use moderateWithBackend instead
 */
async function moderateWithOpenAI(text: string): Promise<ModerationResult> {
  // This function is now deprecated as OpenAI calls are moved to backend
  throw new Error('OpenAI moderation moved to backend');
}

/**
 * Fallback moderation using regex patterns
 */
function moderateWithRegex(text: string): Promise<ModerationResult> {
  const lowerText = text.toLowerCase();
  const flaggedCategories: string[] = [];
  
  // Violence keywords
  const violenceTerms = [
    'kill', 'murder', 'death', 'violence', 'weapon', 'gun', 'knife', 'blood',
    'matar', 'muerte', 'violencia', 'arma', 'sangre', 'cuchillo'
  ];
  
  // Hate speech keywords
  const hateTerms = [
    'hate', 'racist', 'nazi', 'terrorist', 'discrimination',
    'odio', 'racista', 'discriminación', 'terrorista'
  ];
  
  // Sexual content keywords
  const sexualTerms = [
    'sex', 'sexual', 'porn', 'nude', 'naked', 'explicit',
    'sexo', 'sexual', 'desnudo', 'explícito'
  ];
  
  // Harassment keywords
  const harassmentTerms = [
    'stupid', 'idiot', 'loser', 'worthless', 'pathetic',
    'estúpido', 'idiota', 'perdedor', 'inútil', 'patético'
  ];
  
  // Self-harm keywords
  const selfHarmTerms = [
    'suicide', 'self-harm', 'cut yourself', 'end your life',
    'suicidio', 'autolesión', 'cortarte', 'terminar tu vida'
  ];

  // Check for flagged content
  if (violenceTerms.some(term => lowerText.includes(term))) {
    flaggedCategories.push('violence');
  }
  
  if (hateTerms.some(term => lowerText.includes(term))) {
    flaggedCategories.push('hate');
  }
  
  if (sexualTerms.some(term => lowerText.includes(term))) {
    flaggedCategories.push('sexual');
  }
  
  if (harassmentTerms.some(term => lowerText.includes(term))) {
    flaggedCategories.push('harassment');
  }
  
  if (selfHarmTerms.some(term => lowerText.includes(term))) {
    flaggedCategories.push('self_harm');
  }

  return Promise.resolve({
    isSafe: flaggedCategories.length === 0,
    flaggedCategories,
    confidence: flaggedCategories.length > 0 ? 0.8 : 0.1
  });
}

/**
 * Check if a specific message is blocked by admin action
 */
export async function isMessageBlocked(contentId: string): Promise<boolean> {
  try {
    // Use backend API endpoint to check blocked status
    const response = await fetch(`/api/check-message-blocked/${contentId}`);
    
    if (!response.ok) {
      console.error('Failed to check blocked status:', response.statusText);
      return false;
    }
    
    const { isBlocked } = await response.json();
    return isBlocked;
  } catch (error) {
    console.error('Error checking blocked status:', error);
    return false;
  }
}